// src/components/approval/ReferenceModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { X, ChevronDown, Users, Check, UserPlus } from "lucide-react";
import axios from "../../api/axios";
import { listRoles } from "../../api/users/roles";
import { createPortal } from "react-dom";

// 아코디언 회사 컴포넌트
const AccordionCompany = ({
  companyName,
  userCount,
  children,
  onAddAll,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="mb-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            size={16}
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
          <span className="text-sm font-medium text-gray-800">
            {companyName}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs">
            {userCount}명
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddAll();
          }}
          className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 transition-colors"
        >
          전체추가
        </button>
      </button>
      {isOpen && <div className="p-3 space-y-2 bg-white">{children}</div>}
    </div>
  );
};

// 부서 블록 컴포넌트
const DepartmentBlock = ({ dept, users, selectedIds, onToggle, onAddAll }) => {
  return (
    <div className="pl-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">{dept}</span>
        <button
          type="button"
          onClick={() => onAddAll(users.map((u) => u.id))}
          className="px-2 py-1 rounded-md bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 transition-colors"
        >
          전체추가
        </button>
      </div>
      <div className="space-y-1">
        {users.map((user) => {
          const selected = selectedIds.includes(user.id);
          return (
            <button
              key={user.id}
              type="button"
              onClick={() => onToggle(user.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                selected
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-gray-50 hover:bg-gray-100 text-gray-800"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">
                    {user.last_name}
                    {user.first_name}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    {user.position}
                  </span>
                </div>
                {selected && <Check size={16} className="text-green-500" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// 역할 필터 칩 컴포넌트
const RoleChip = ({ role, isActive, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
      isActive
        ? "bg-green-500 text-white"
        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
    }`}
  >
    {role}
  </button>
);

const ReferenceModal = ({
  isOpen,
  onClose,
  selectedIds = [],
  onChange,
  approvalLines = [],
  onSave = () => {},
  name = "참조인",
  users: usersProp,
  scheduleType,
  companyId,
  single = false,
}) => {
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];
  const [referenceList, setReferenceList] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCompanies, setOpenCompanies] = useState({});
  const [activeTab, setActiveTab] = useState("users"); // 'users' | 'selected'
  const projectId = localStorage.getItem("projectId");
  const [selectedProjectRoles, setSelectedProjectRoles] = useState([]);

  const toggleCompany = (company) => {
    setOpenCompanies((prev) => ({ ...prev, [company]: !prev[company] }));
  };

  const buildSelectedUserDetails = () =>
    safeSelectedIds
      .map((id) => users.find((u) => u.id === id))
      .filter(Boolean)
      .map(({ id, last_name, first_name, position, company }) => ({
        id,
        last_name,
        first_name,
        position,
        company,
      }));

  const toggleProjectRole = (role) =>
    setSelectedProjectRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  useEffect(() => {
    const loadRoles = async () => {
      try {
        await listRoles({ project: projectId, page_size: 1000 });
      } catch (e) {
        console.error("Failed to load roles", e);
      }
    };
    loadRoles();
  }, [projectId]);

  const usersPropKey = useMemo(() => {
    if (!Array.isArray(usersProp) || usersProp.length === 0) return "";
    return usersProp.map((u) => u.id).join(",");
  }, [usersProp]);

  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(usersProp) && usersProp.length > 0) {
      // No schedule-type filtering; allow all users
      setUsers(usersProp);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`/project/${projectId}/users-without-me/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        });
        if (cancelled) return;

        const list = Array.isArray(res.data?.results) ? res.data.results : res.data ?? [];
        setUsers(list);
      } catch (e) {
        if (!cancelled) console.error("Failed to load users", e);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, usersPropKey, projectId, scheduleType, companyId]);

  const approvalIdsKey = useMemo(
    () =>
      (approvalLines || [])
        .map((a) => String(a.approver_id ?? a.id ?? ""))
        .join(","),
    [approvalLines]
  );

  useEffect(() => {
    if (!isOpen) return;

    const approvalIds = new Set(
      (approvalLines || []).map((a) => String(a.approver_id ?? a.id))
    );
    let list = users.filter((u) => !approvalIds.has(String(u.id)));

    // Debug logging to verify filtering behavior
    console.log('ReferenceModal: scheduleType', scheduleType, 'companyId', companyId);
    console.log('ReferenceModal: initial list length (after approval filter)', list.length);

    // Removed company-specific filtering; all users are allowed for any schedule type
    // if (scheduleType === "company" && companyId) {
    //   list = list.filter((u) => Number(u.company_id) === Number(companyId));
    // }

    if (selectedProjectRoles.length > 0) {
      list = list.filter((u) =>
        (u.project_role || []).some((r) => selectedProjectRoles.includes(r))
      );
    }

    console.log('ReferenceModal: final list length after role filter', list.length);
    setReferenceList(list);
  }, [
    isOpen,
    users,
    selectedProjectRoles,
    scheduleType,
    companyId,
    approvalIdsKey,
  ]);

  if (!isOpen) return null;

  // 회사 → 부서 그룹핑
  const grouped = {};
  referenceList.forEach((u) => {
    const company = u.company || "미지정 회사";
    const dept = u.department || "부서 없음";
    if (!grouped[company]) grouped[company] = {};
    if (!grouped[company][dept]) grouped[company][dept] = [];
    grouped[company][dept].push(u);
  });

  // 사용 가능한 역할 목록
  const availableRoles = [
    ...new Set(users.flatMap((u) => u.project_role || [])),
  ];

  const handleAddAllFromCompany = (company) => {
    const depts = grouped[company];
    const ids = Object.values(depts)
      .flat()
      .map((u) => u.id);
    if (single) {
      onChange(ids.length ? [ids[0]] : []);
    } else {
      onChange([...new Set([...safeSelectedIds, ...ids])]);
    }
  };

  const modal = (
    <>
      {/* 배경 오버레이 */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* 모달 컨테이너 */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[700px] flex flex-col overflow-hidden pointer-events-auto">
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-bold text-gray-900">{name} 관리</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* 모바일 탭 */}
          <div className="flex md:hidden border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab("users")}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === "users"
                  ? "text-green-600 border-b-2 border-green-600 bg-green-50"
                  : "text-gray-500"
              }`}
            >
              사용자 목록
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("selected")}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === "selected"
                  ? "text-green-600 border-b-2 border-green-600 bg-green-50"
                  : "text-gray-500"
              }`}
            >
              선택된 {name}
              {safeSelectedIds.length > 0 && (
                <span className="absolute top-2 right-4 px-1.5 py-0.5 rounded-full bg-green-500 text-white text-xs">
                  {safeSelectedIds.length}
                </span>
              )}
            </button>
          </div>

          {/* 본문 */}
          <div className="flex flex-1 overflow-hidden">
            {/* 왼쪽: 사용자 목록 */}
            <div
              className={`w-full md:w-1/2 bg-gray-50 overflow-y-auto border-r border-gray-200 ${
                activeTab !== "users" ? "hidden md:block" : ""
              }`}
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Users size={16} />
                  사용자 목록
                </h3>

                {/* 역할 필터 */}
                {availableRoles.length > 0 && (
                  <div className="mb-4 overflow-hidden">
                    <label className="text-xs text-gray-600 mb-2 block">
                      회사 역할 필터
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <RoleChip
                        role="전체"
                        isActive={selectedProjectRoles.length === 0}
                        onClick={() => setSelectedProjectRoles([])}
                      />
                      {availableRoles.map((role) => (
                        <RoleChip
                          key={role}
                          role={role}
                          isActive={selectedProjectRoles.includes(role)}
                          onClick={() => toggleProjectRole(role)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* 회사별 아코디언 */}
                {Object.entries(grouped).map(([company, depts]) => (
                  <AccordionCompany
                    key={company}
                    companyName={company}
                    userCount={Object.values(depts).flat().length}
                    isOpen={openCompanies[company]}
                    onToggle={() => toggleCompany(company)}
                    onAddAll={() => handleAddAllFromCompany(company)}
                  >
                    {Object.entries(depts).map(([dept, deptUsers]) => (
                      <DepartmentBlock
                        key={dept}
                        dept={dept}
                        users={deptUsers}
                        selectedIds={safeSelectedIds}
                        onToggle={(id) => {
                          if (single) {
                            onChange([id]);
                          } else {
                            onChange(
                              safeSelectedIds.includes(id)
                                ? safeSelectedIds.filter((x) => x !== id)
                                : [...safeSelectedIds, id]
                            );
                          }
                        }}
                        onAddAll={(ids) => {
                          if (single) {
                            onChange([ids[0]]);
                          } else {
                            onChange([
                              ...new Set([...safeSelectedIds, ...ids]),
                            ]);
                          }
                        }}
                      />
                    ))}
                  </AccordionCompany>
                ))}
              </div>
            </div>

            {/* 오른쪽: 선택된 참조인 */}
            <div
              className={`w-full md:w-1/2 bg-white overflow-y-auto ${
                activeTab !== "selected" ? "hidden md:block" : ""
              }`}
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <UserPlus size={16} />
                  선택된 {name} ({safeSelectedIds.length}명)
                </h3>

                {safeSelectedIds.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <UserPlus size={24} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">
                      왼쪽에서
                      <br />
                      사용자를 선택하세요.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {safeSelectedIds.map((id) => {
                      const u = users.find((x) => x.id === id);
                      if (!u) return null;
                      return (
                        <div
                          key={id}
                          className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200"
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {u.last_name}
                              {u.first_name}
                              <span className="text-gray-500 font-normal ml-2">
                                {u.position}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {u.company}
                              {u.department ? ` · ${u.department}` : ""}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              onChange(safeSelectedIds.filter((x) => x !== id))
                            }
                            className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={async () => {
                const ok = await onSave(
                  [...safeSelectedIds],
                  buildSelectedUserDetails()
                );
                if (ok !== false) onClose();
              }}
              className="px-4 py-2.5 rounded-xl bg-green-500 text-sm font-medium text-white hover:bg-green-600 transition-colors"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
};

export default ReferenceModal;
